import { Fragment } from "react";
import { css, cx } from "styled-system/css";
import { ak } from "./admin-styles";
import { Status, Time, type StatusKind } from "~/components/editorial";
import { formatDurationMs } from "./sync-status";

// Shared pieces for the Jira and Cursor connection pages: one status per
// instance and a ruled sync-history table.

export interface SyncLogRow {
  id: string;
  sourceName: string;
  syncType: string;
  status: string;
  startedAt: string;
  duration: number | null;
  itemsSynced: number;
  errorMessage: string | null;
}

const LOG_STATUS: Record<string, { kind: StatusKind; label: string }> = {
  completed: { kind: "ok", label: "Completed" },
  partial: { kind: "fail", label: "Partial" },
  failed: { kind: "fail", label: "Failed" },
  started: { kind: "busy", label: "Running" },
};

export function SyncLogTable({
  logs,
  itemsLabel = "Items",
}: {
  logs: SyncLogRow[];
  itemsLabel?: string;
}) {
  if (logs.length === 0) {
    return (
      <div className={ak.empty}>
        <p>No sync history yet.</p>
      </div>
    );
  }
  return (
    <div className={ak.tableWrap}>
      <table className={cx(ak.table, styles.table)}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Source</th>
            <th>Type</th>
            <th>Started</th>
            <th data-num>Duration</th>
            <th data-num>{itemsLabel}</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => {
            const st = LOG_STATUS[log.status] ?? {
              kind: "wait" as const,
              label: log.status,
            };
            return (
              <Fragment key={log.id}>
                <tr data-has-detail={log.errorMessage ? true : undefined}>
                  <td>
                    <Status kind={st.kind}>{st.label}</Status>
                  </td>
                  <td>{log.sourceName}</td>
                  <td className={ak.mono}>{log.syncType}</td>
                  <td>
                    <Time iso={log.startedAt} />
                  </td>
                  <td data-num>{formatDurationMs(log.duration)}</td>
                  <td data-num>{log.itemsSynced.toLocaleString()}</td>
                </tr>
                {log.errorMessage && (
                  <tr data-detail>
                    <td colSpan={6}>{log.errorMessage}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  table: css({
    "& tr[data-has-detail] td": { borderBottom: "none" },
    "& tr[data-detail] td": {
      pt: "0",
      fontFamily: "mono",
      fontSize: "11.5px",
      color: "ed.ink2",
      overflowWrap: "anywhere",
    },
  }),
};
